import { useState } from 'react';
import { X, Clock, User, MapPin, Users, Calendar, CheckCircle, MessageCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { formatDate, getWhatsAppLink } from '../data/mockData';

export const BookingDrawer = ({ isOpen, onClose, classData }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  if (!classData) return null;

  const isWaitlist = classData.status === 'full';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    setStep(2);
    
    toast.success(
      isWaitlist 
        ? '¡Te agregamos a la lista de espera!' 
        : '¡Reserva confirmada!',
      {
        description: isWaitlist 
          ? 'Te notificaremos si se libera un lugar.'
          : 'Recibirás confirmación por WhatsApp.',
      }
    );
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ name: '', email: '', phone: '' });
    onClose();
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Principiante',
      intermediate: 'Intermedio',
      advanced: 'Avanzado',
      all: 'Todos los niveles',
    };
    return labels[level];
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent 
        side="bottom" 
        hideClose
        className="h-[90vh] sm:h-[85vh] max-h-[92dvh] rounded-t-3xl p-0 overflow-hidden flex flex-col"
        data-testid="booking-drawer"
      >
        {/* Header */}
        <SheetHeader className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading text-lg sm:text-xl">
              {step === 1 
                ? (isWaitlist ? 'Lista de espera' : 'Reservar clase') 
                : '¡Listo!'}
            </SheetTitle>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="close-drawer-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 no-scrollbar">
          {step === 1 ? (
            <>
              {/* Class Info Card */}
              <div className="bg-slate-50 rounded-2xl p-4 mb-6">
                <h3 className="font-heading font-bold text-base sm:text-lg text-slate-900 mb-3">
                  {classData.name}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span className="capitalize">{formatDate(classData.date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>{classData.time} ({classData.duration} min)</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <User className="w-4 h-4" />
                    <span>{classData.coach}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="w-4 h-4" />
                    <span>
                      {isWaitlist 
                        ? 'Clase llena' 
                        : `${classData.availableSpots} lugares disponibles`}
                    </span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <span className={`level-badge ${classData.level}`}>
                    {getLevelLabel(classData.level)}
                  </span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Nombre completo
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                    data-testid="booking-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                    data-testid="booking-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                    WhatsApp
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+52 55 1234 5678"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                    data-testid="booking-phone-input"
                  />
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap gap-3 py-2 text-xs sm:text-sm">
                  <span className="trust-badge">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Cancelación fácil
                  </span>
                  <span className="trust-badge">
                    <MessageCircle className="w-4 h-4 text-[#25D366]" />
                    Confirmación por WhatsApp
                  </span>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary h-12 sm:h-14 text-sm sm:text-base"
                  data-testid="confirm-booking-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Procesando...
                    </span>
                  ) : (
                    isWaitlist ? 'Unirme a lista de espera' : 'Confirmar reserva'
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#16263B] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 mb-2">
                {isWaitlist ? '¡Estás en la lista!' : '¡Reserva confirmada!'}
              </h3>
              <p className="text-slate-500 mb-8">
                {isWaitlist 
                  ? `Te notificaremos por WhatsApp si se libera un lugar en ${classData.name}.`
                  : `Te esperamos el ${formatDate(classData.date)} a las ${classData.time}.`}
              </p>

              {/* Class Summary */}
              <div className="bg-slate-50 rounded-2xl p-4 mb-6 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#16263B] rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{classData.name}</p>
                    <p className="text-sm text-slate-500">
                      {classData.time} - {classData.coach}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <a
                  href={getWhatsAppLink(`Hola, acabo de reservar la clase ${classData.name} para el ${formatDate(classData.date)}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn w-full justify-center"
                  data-testid="success-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Ver confirmación en WhatsApp
                </a>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="w-full h-12 rounded-full"
                  data-testid="close-success-btn"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
